import React, { useRef, useEffect, useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import '../styles/hero.css';

const VIDEO_SRC = `${process.env.PUBLIC_URL}/try1.mp4`;
const FADE_DURATION = 2; // seconds for fade out/in
const BLACK_DURATION = 2; // seconds of black between fades

function Hero() {
  const ref = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [videoOpacity, setVideoOpacity] = useState(0.6);
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const hasLoopedRef = useRef(false);
  useScrollReveal(ref);

  // Scroll tracking
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth opacity animation near loop boundaries
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Track when the video loops so we skip fades on first play
    const handleSeeked = () => {
      if (video.currentTime < 1) {
        hasLoopedRef.current = true;
      }
    };
    video.addEventListener('seeked', handleSeeked);

    const tick = () => {
      const { currentTime, duration } = video;
      if (!duration) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // First play cycle: skip fade, show video immediately
      if (!hasLoopedRef.current) {
        setVideoOpacity(0.6);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const timeToEnd = duration - currentTime;

      if (timeToEnd <= FADE_DURATION) {
        // Fading out before loop
        const progress = timeToEnd / FADE_DURATION;
        setVideoOpacity(0.6 * progress);
      } else if (currentTime <= BLACK_DURATION + FADE_DURATION) {
        if (currentTime <= BLACK_DURATION) {
          // Black gap after loop restart
          setVideoOpacity(0);
        } else {
          // Fading in after black gap
          const progress = (currentTime - BLACK_DURATION) / FADE_DURATION;
          setVideoOpacity(0.6 * progress);
        }
      } else {
        setVideoOpacity(0.6);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    video.play().catch(() => {});
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, []);

  const parallaxOffset = scrollY * 0.3;
  const heroOpacity = Math.max(0, 1 - scrollY / 800);

  return (
    <section className="hero" ref={ref}>
      <div className="hero-bg">
        <video
          ref={videoRef}
          className="hero-video"
          style={{ opacity: videoOpacity }}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
        <div className="hero-video-overlay" />
        <div className="hero-shape-circle" />
        <div className="hero-shape-triangle" />
        <div className="hero-shape-line hero-shape-line--1" />
        <div className="hero-shape-line hero-shape-line--2" />
        <div className="hero-shape-line hero-shape-line--3" />
        <div className="hero-grid" />
      </div>
      <div
        className="hero-content"
        style={{
          transform: `translateY(${parallaxOffset}px)`,
          opacity: heroOpacity,
        }}
      >
        <span className="hero-tag reveal">Quantena AG — Zug, Switzerland</span>
        <h1 className="hero-headline reveal">
          {"Building\n"}
          <em>{"incorruptible"}</em>
          {"\nfinancial\nmachines."}
        </h1>
        <p className="hero-subtitle reveal">
          Pioneering infrastructure for the next generation of finance.
        </p>
      </div>
      <div className="hero-scroll-indicator" style={{ opacity: heroOpacity }}>
        <span className="hero-scroll-text">Scroll</span>
        <div className="hero-scroll-line">
          <div className="hero-scroll-dot" />
        </div>
      </div>
    </section>
  );
}

export default Hero;
